/**
 * Pick the buy-bridge variant to surface for a calculator-chosen vial dose.
 *
 * Calculator presets rarely line up with the discrete Saleor variant doses, so a
 * strict equality match returns null for most users. Instead, prefer in order:
 *   1. exact dose match,
 *   2. the smallest variant whose dose is >= the wanted mg (upsell to next size up),
 *   3. the largest available variant (dose exceeds every option).
 *
 * Variants with a null `doseMg` are ignored. Returns null only when no variant has
 * a usable dose (so callers can fall back to the "from"-price / learn-more path).
 */
export function pickVariantForDose<T extends { doseMg: number | null }>(
	variants: readonly T[],
	mg: number,
): T | null {
	const dosed = variants.filter((v): v is T & { doseMg: number } => v.doseMg !== null);
	if (dosed.length === 0) return null;

	const exact = dosed.find((v) => v.doseMg === mg);
	if (exact) return exact;

	const ascending = [...dosed].sort((a, b) => a.doseMg - b.doseMg);
	const nextUp = ascending.find((v) => v.doseMg >= mg);
	if (nextUp) return nextUp;

	return ascending[ascending.length - 1]!;
}
